/**
 * Simple debounce hook for delaying value updates
 * Useful for search inputs to avoid excessive API calls
 */

import { useState, useEffect } from 'react'

/**
 * Debounces a value by delaying updates until the value stops changing
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 500ms)
 * @returns Debounced value
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}
